import { rxMutation, withEntityResources, withMutations } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EnvironmentService, ErrorHandlerService, FlashMessageService, SpinnerOverlayService } from '@libs/shared/common';
import { FileModel, ORDERS_SHOPPING_LISTS_ROUTE_PATH } from '@libs/shared/models';
import { ProductListPayloadModel, ShopErrorCollectionModel, ShoppingListItemModel, ShoppingListModel } from '@libs/shop/models';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { prependEntity, removeEntity, updateEntity } from '@ngrx/signals/entities';
import { firstValueFrom, map } from 'rxjs';
import { FileSaverService } from '../../file-saver';
import { ShoppingListsRepositoryService } from '../shopping-lists-repository.service';

@Injectable({ providedIn: 'root' })
export class ShoppingListsState extends signalStore(
  withProps(() => ({
    repository: inject(ShoppingListsRepositoryService),
    router: inject(Router),
    messageService: inject(FlashMessageService),
    loader: inject(SpinnerOverlayService),
    environmentService: inject(EnvironmentService),
    fileSaver: inject(FileSaverService),
    errorHandler: inject(ErrorHandlerService),
  })),
  withState({}),
  withEntityResources((store) => rxResource({
    stream: () => store.repository.initShoppingLists(),
    defaultValue: [],
  })),
  withMethods((store) => ({
    downloadShoppingList: async (listId: string): Promise<string> => {
      try {
        const file: FileModel = await firstValueFrom(store.repository.downloadShoppingList(listId));

        await store.fileSaver.saveBlob(file.blob, file.filename);

        return file.filename;
      } catch (error: unknown) {
        store.errorHandler.handleError(error);
        store.messageService.showMessage('errorListDownload');

        throw error;
      }
    },
    handleDeleteResponse: (params: { listId: string; items: ShoppingListItemModel[]; response: ShopErrorCollectionModel }): void => {
      if (params.response.errors.length) {
        const failedSkusText: string = params.response.errors.map(item => item.itemPointer ?? item.message).join(', ');
        const failedSkus: string[] = params.response.errors.map(item => item.itemPointer).filter(x => Boolean(x)) as string[];

        store.messageService.showCustomMessage({
          message: `Folgende Artikel konnten nicht entfernt werden: ${failedSkusText}`,
          theme: 'warning',
          duration: 10000,
        });

        params.items = params.items.filter(x => !failedSkus.includes(x.sku));
      }

      const itemIds: string[] = params.items.map(x => x.id);
      const newItems: ShoppingListItemModel[] = store.entityMap()[params.listId].items.filter(x => !itemIds.includes(x.id));

      patchState(store, updateEntity({ id: params.listId, changes: { items: newItems, itemCount: newItems.length } }));
    },
  })),
  withComputed((store) => ({
    shoppingLists: (): ShoppingListModel[] => {
      return store.entities().filter(x => !x.isQuickOrder);
    },
  })),
  withMutations((store) => ({
    createNewShoppingList: rxMutation({
      operation: (name: string) => store.repository.createShoppingList(name),
      onSuccess: (result) => {
        store.messageService.showMessage('successCreateShoppingList');
        patchState(store, prependEntity(result));
      },
      onError: () => {
        store.messageService.showMessage('errorCreateShoppingList');
      },
    }),
    updateShoppingList: rxMutation({
      operation: (list: ShoppingListModel) => store.repository.updateShoppingList(list),
      onSuccess: (result) => {
        store.messageService.showMessage('successUpdateShoppingList');
        patchState(store, updateEntity({ id: result.id, changes: { name: result.name } }));
      },
      onError: () => {
        store.messageService.showMessage('errorUpdateShoppingList');
      },
    }),
    deleteShoppingList: rxMutation({
      operation: (id: string) => store.repository.deleteShoppingList(id).pipe(map(() => id)),
      onSuccess: (deletedShoppingListId: string) => {
        store.messageService.showMessage('successRemoveShoppingList');
        patchState(store, removeEntity(deletedShoppingListId));
      },
      onError: () => {
        store.messageService.showMessage('errorRemoveShoppingList');
      },
    }),
    addProductToShoppingList: rxMutation({
      operation: (params: {
        listId: string;
        sku: string;
        quantity: number;
      }) => store.repository.addProductToShoppingList(params.listId, params.sku, params.quantity),
      onSuccess: (result, params) => {
        store.messageService.showMessage('successAddToShoppingList');
        const newItems: ShoppingListItemModel[] = store.entityMap()[params.listId].items;
        const index: number = newItems.findIndex(x => x.id === result.id);

        if (index === -1) {
          newItems.push(result);
        } else {
          newItems[index] = result;
        }

        patchState(store, updateEntity({ id: params.listId, changes: { items: newItems.concat(), itemCount: newItems.length } }));
      },
      onError: () => {
        store.messageService.showMessage('errorAddToShoppingList');
      },
    }),
    addProductCollectionToShoppingList: rxMutation({
      operation: (params: {
        listId: string;
        collection: ProductListPayloadModel[];
      }) => store.repository.addProductCollectionToShoppingList(params.listId, params.collection),
      onSuccess: (result, params) => {
        if (result.errors.length) {
          const failedSkus: string = result.errors.map(item => item.itemPointer ?? item.message).join(', ');

          store.messageService.showCustomMessage({
            message: `Folgende Artikel konnten nicht hinzugefügt werden: ${failedSkus}`,
            theme: 'warning',
            duration: 10000,
          });
        } else {
          store.messageService.showMessage('successAddToShoppingList');
        }

        const items: ShoppingListItemModel[] = store.entityMap()[params.listId].items;
        const updatedItemsMap: Map<string, ShoppingListItemModel> = new Map(items.map(item => [item.id, item]));

        for (const item of result.value.items) {
          updatedItemsMap.set(item.id, item);
        }

        const updatedItems: ShoppingListItemModel[] = Array.from(updatedItemsMap.values());

        patchState(store, updateEntity({ id: params.listId, changes: { items: updatedItems.concat(), itemCount: updatedItems.length } }));
      },
      onError: () => {
        store.messageService.showMessage('errorAddToShoppingList');
      },
    }),
    updateProductInShoppingList: rxMutation({
      operation: (params: {
        listId: string;
        item: ShoppingListItemModel;
      }) => store.repository.updateProductInShoppingList(params.listId, params.item),
      onSuccess: (result, params) => {
        store.messageService.showMessage('successUpdateShoppingList');
        const newItems: ShoppingListItemModel[] = store.entityMap()[params.listId].items;
        const index: number = newItems.findIndex(x => x.id === result.id);

        newItems[index] = result;
        patchState(store, updateEntity({ id: params.listId, changes: { items: newItems.concat() } }));
      },
      onError: () => {
        store.messageService.showMessage('errorUpdateShoppingList');
      },
    }),
    deleteProductFromShoppingList: rxMutation({
      operation: (params: {
        listId: string;
        item: ShoppingListItemModel;
      }) => store.repository.deleteProductFromShoppingList(params.listId, params.item),
      onSuccess: (_r, params) => {
        store.messageService.showMessage('successRemoveFromShoppingList');
        const newItems: ShoppingListItemModel[] = store.entityMap()[params.listId].items.filter(x => x.id !== params.item.id);

        patchState(store, updateEntity({ id: params.listId, changes: { items: newItems, itemCount: newItems.length } }));
      },
      onError: () => {
        store.messageService.showMessage('errorRemoveFromShoppingList');
      },
    }),
    deleteProductsFromShoppingListCollection: rxMutation({
      operation: (params: {
        listId: string;
        items: ShoppingListItemModel[];
      }) => store.repository.deleteProductsFromShoppingListCollection(params.listId, params.items),
      onSuccess: (result, params) => {
        if (!result.errors.length) {
          store.messageService.showMessage('successRemoveFromShoppingList');
        }

        store.handleDeleteResponse({ ...params, response: result });
      },
      onError: () => {
        store.messageService.showMessage('errorRemoveFromShoppingList');
      },
    }),
    deleteProductsFromShoppingListCollectionSilent: rxMutation({
      operation: (params: {
        listId: string;
        items: ShoppingListItemModel[];
      }) => store.repository.deleteProductsFromShoppingListCollection(params.listId, params.items),
      onSuccess: (result, params) => {
        store.handleDeleteResponse({ ...params, response: result });
      },
      onError: () => {
        store.messageService.showMessage('errorRemoveFromShoppingList');
      },
    }),
    loadShoppingListById: rxMutation({
      operation: (id: string) => store.repository.getShoppingListById(id),
      onSuccess: (result) => {
        patchState(store, updateEntity({ id: result.id, changes: result }));
      },
    }),
  })),
  withMethods((store) => ({
    navigateToDetail: async (id: string): Promise<void> => {
      await store.router.navigate([ORDERS_SHOPPING_LISTS_ROUTE_PATH, id]);
    },
    reload: (): void => {
      store._reload();
    },
  })),
  withProps((store) => ({
    mutationIsLoading: computed(() =>
      store.loadShoppingListByIdIsPending() ||
      store.updateShoppingListIsPending() ||
      store.deleteShoppingListIsPending() ||
      store.createNewShoppingListIsPending() ||
      store.addProductToShoppingListIsPending() ||
      store.addProductCollectionToShoppingListIsPending() ||
      store.updateProductInShoppingListIsPending() ||
      store.deleteProductFromShoppingListIsPending() ||
      store.deleteProductsFromShoppingListCollectionIsPending(),
    ),
  })),
  withProps((store) => ({
    // when running in web components context we need to hide the spinner for loading the lists as on each page switch the site gets reloaded and spinner is shown every time.
    showLoader: computed(() => store.isLoading() || store.mutationIsLoading()),
  })),
  withHooks(store => ({
    onInit(): void {
      effect(() => {
        if (store.showLoader()) {
          store.loader.showLoader();
        } else {
          store.loader.hideLoader();
        }
      });
    },
  })),
) {
}
